import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CustomerLoader, PhoneInput } from '../../../components';
import styles from './team-member-submit-details-page.module.scss';
import CustomerPageLayout from '../../../customer-page-layout/CustomerPageLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApplicationRoutes } from '../../../../../routes';
import { useLazyGetTeamMemberInviteByIdQuery, useUpdateDetailsTeamMemberInvitationMutation, useVerifyOtpTeamMemberInvitationMutation } from '../../../../../service/team-management-service';
import { toast } from 'react-toastify';
import { validatePhoneNumber } from '../../../validators';
import { validatePassword } from '../../../customer-util';
import { PasscodeOTPInput } from '../../../onboarding/components';
import Button from '../../../../../common/ekedc-components/button/Button';
import Input from '../../../../../common/ekedc-components/input/Input';
import { useForceLightTheme } from '../../../../../theme/useForceLightTheme';
import { useDispatch } from 'react-redux';
import { logIn } from '../../../../../stores/slices/current-user-slice';
import { UserType } from '../../../../../model/userType';
import { useTenant } from '../../../../../tenant/branding/useTenant';

type TeamMemberSubmitDetailsFormProps = {
  phoneNumber: string;
  password: string;
  confirmPassword: string;
};

export const TeamMemberSubmitDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { name: tenantName, countryCode } = useTenant();

  // The shared Input/Button are light-only, as on signin and signup.
  useForceLightTheme();

  const [getInviteDetails, { isLoading: isLoadingDetails, isFetching: isFetchingDetails }] = useLazyGetTeamMemberInviteByIdQuery();
  const [updateDetailsTeamMemberInvitation, { isLoading: isUpdatingDetails }] = useUpdateDetailsTeamMemberInvitationMutation();
  const [verifyOtpTeamMemberInvitation, { isLoading: isVerifyingOtp }] = useVerifyOtpTeamMemberInvitationMutation();

  const [memberData, setMemberData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // OTP verification states
  const [isOTPVerification, setIsOTPVerification] = useState(false);
  const [otp, setOtp] = useState<string>("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TeamMemberSubmitDetailsFormProps>({
    defaultValues: {
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    },
    reValidateMode: 'onChange',
  });
  const password = watch('password');
  const phoneNumber = watch('phoneNumber');

  useEffect(() => {
    if (!id) {
      toast.error(t("account.web.failedToParseInviteId"));
      return;
    }

    const fetchInviteDetails = async () => {
      try {
        const response = await getInviteDetails(id).unwrap();

        setMemberData({
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
        });
      } catch (error: any) {
        toast.error(error?.data?.title || t("account.web.failedToFetchInviteDetails"));
      }
    };
    fetchInviteDetails();
  }, [id, getInviteDetails]);

  const onSubmit = async (data: TeamMemberSubmitDetailsFormProps) => {
    if (!id) return;

    try {
      await updateDetailsTeamMemberInvitation({
        inviteId: id,
        phoneNumber: data.phoneNumber,
        password: data.password,
      }).unwrap();

      toast.success(t('account.web.detailsSubmittedSuccessfully'));
      setIsOTPVerification(true);
    } catch (error: any) {
      toast.error(error?.data?.title || t('account.web.failedToSubmitDetails'));
    }
  };

  const handleResendOTP = async () => {
    if (!id) return;

    try {
      await updateDetailsTeamMemberInvitation({
        inviteId: id,
        phoneNumber: phoneNumber,
        password: password,
      }).unwrap();

      setOtp("");
      toast.success(t('account.web.resentOtpSuccessfully'));
    } catch (error: any) {
      toast.error(error?.data?.title || t('account.web.failedToSubmitDetails'));
    }
  };

  const handleOTPSubmit = async () => {
    if (otp.length !== 4) {
      toast.error(t("account.web.enterValid4DigitOtp"));
      return;
    }

    try {
      const response = await verifyOtpTeamMemberInvitation({
        inviteId: id || "",
        otp: otp
      }).unwrap();

      toast.success(t("account.web.otpVerifiedSuccessfully"));
      dispatch(logIn({ token: response.token, refreshToken: response.refreshToken, userType: UserType.Customer }));
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.invalidOtpCode"));
      setOtp("");
    }
  };

  const handleCancelOTPVerification = () => {
    setIsOTPVerification(false);
    setOtp("");
  };

  if (isLoadingDetails || isFetchingDetails) {
    return (
      <CustomerPageLayout>
        <CustomerLoader
          title={t("account.web.loadingInviteDetails")}
          subtitle={t("account.web.pleaseWaitFetchingInvitationDetails")}
        />
      </CustomerPageLayout>
    );
  }

  if (isOTPVerification) return (
    <CustomerPageLayout title={t("common.labels.phoneNumberVerification")} onBack={handleCancelOTPVerification}>
      <div className={styles.stepContent}>
        <PasscodeOTPInput
          value={otp}
          onChange={setOtp}
          onResend={handleResendOTP}
          otpLength={4}
          resendTimerSeconds={180}
          disabled={isVerifyingOtp || isUpdatingDetails}
          sendToEmail={false}
          contact={phoneNumber}
          dialCode=""
          renderSeparator={false}
          className={styles.themedOtp}
        />

        <div className={styles.buttonSection}>
          <Button
            text={t("solar.energyRequirements.confirm")}
            onClick={handleOTPSubmit}
            loading={isVerifyingOtp}
            loadingText={t("account.web.verifying")}
            disabled={otp.length !== 4}
          />
        </div>
      </div>
    </CustomerPageLayout>
  )

  return (
    <CustomerPageLayout
      title={t("corporate.welcomeFamilyTeamMember", { tenant: tenantName })}
      subtitle={t("corporate.pleaseFillDetails")}
      showBackButton={false}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.stepContent}>
        <div className={styles.formFields}>
          <div className={`${styles.card} ${styles.flexGapSection}`}>
            <div className={styles.cardHeader}>
              <span className={styles.cardLabel}>{t("auth.personalInformation")}</span>
            </div>
            <div className={styles.nameRow}>
              <Input label={t("common.labels.firstName")} value={memberData.firstName} disabled />
              <Input label={t("common.labels.lastName")} value={memberData.lastName} disabled />
            </div>
            <Input label={t("account.email")} type="email" value={memberData.email} disabled />
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardLabel}>{t("common.labels.phoneNumber")}</span>
            </div>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: t("account.web.phoneNumberIsRequired"),
                validate: (value) => {
                  return validatePhoneNumber(value);
                }
              }}
              render={({ field, fieldState }) => (
                <PhoneInput
                  value={field.value}
                  onChange={(phone) => field.onChange(phone)}
                  defaultCountry={countryCode}
                  lockCountry
                  placeholder={t("account.web.enterPhoneNumber")}
                  error={fieldState.error?.message}
                  // className={styles.phoneWrapper}
                />
              )}
            />
          </div>

          <div className={`${styles.card} ${styles.flexGapSection}`}>
            <div className={styles.cardHeader}>
              <span className={styles.cardLabel}>{t("common.labels.password")}</span>
            </div>
            <Controller
              name="password"
              control={control}
              rules={{
                required: t('corporate.passwordRequired'),
                minLength: { value: 10, message: t('account.web.passwordMinLength10') },
                validate: value => validatePassword(value),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="password"
                  placeholder={t('account.web.createPassword')}
                  error={errors.password?.message}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: t('account.web.pleaseConfirmYourPassword'),
                validate: (value) =>
                  value === password || t('common.validation.passwordsDoNotMatch'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="password"
                  placeholder={t('common.labels.confirmPassword')}
                  error={errors.confirmPassword?.message}
                />
              )}
            />

            <div className={styles.fieldDivider} />
            <div className={styles.passwordRequirements}>
              <span>{t("common.labels.passwordRequirementsTitle")}</span>
              <span>{t("auth.web.useTenToSixtyFourCharacters")}</span>
              <span>- {t("auth.oneUppercaseLetter")}</span>
              <span>- {t("auth.web.oneLowercaseLetter")}</span>
              <span>- {t("auth.web.oneNumber")}</span>
              <span>- {t("auth.web.oneSpecialCharacterExample")}</span>
              <span>{t("auth.web.avoidUsingEmailPart")}</span>
            </div>
          </div>
        </div>

        <div className={styles.buttonSection}>
          <div className={styles.buttonRow}>
            <Button
              variant="secondary"
              text={t("common.actions.cancel")}
              onClick={() => navigate(`/${ApplicationRoutes.DASHBOARD}`)}
            />
            <Button
              type="submit"
              text={t("corporate.submitDetails")}
              loading={isUpdatingDetails}
              loadingText={t("account.web.submittingDetails")}
            />
          </div>
        </div>
      </form>
    </CustomerPageLayout>
  );
};
